import { useId } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import type { Customization } from '@nucleus-commerce/api-client/admin';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import { FormField } from '@nucleus-commerce/ui/components/form';
import { Label } from '@nucleus-commerce/ui/components/label';

import type { ProductTypeFormInput } from '../schemas/product-type-form-schema';

export interface ProductTypeCustomizationsSectionProps {
  /** The organization's customization dictionary, in its own order. */
  customizations: readonly Customization[];
  readOnly: boolean;
}

/** Multi-select over the customization dictionary; ids are sent in dictionary order. */
export function ProductTypeCustomizationsSection({
  customizations,
  readOnly,
}: ProductTypeCustomizationsSectionProps) {
  const { t } = useTranslation('productTypes');
  const form = useFormContext<ProductTypeFormInput>();
  const groupId = useId();

  return (
    <FormSection
      title={t('sections.customizations')}
      description={t('sections.customizationsDescription')}
    >
      <FormField
        control={form.control}
        name="defaultCustomizationIds"
        render={({ field }) => {
          const chosen = new Set(field.value);
          // An id the dictionary no longer lists is kept as it is, not silently dropped.
          const unknown = field.value.filter((id) => !customizations.some((c) => c.id === id));
          const toggle = (id: string, on: boolean) =>
            field.onChange([
              ...customizations.flatMap((c) =>
                (c.id === id ? on : chosen.has(c.id)) ? [c.id] : [],
              ),
              ...unknown,
            ]);
          return customizations.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t('customizations.none')}</p>
          ) : (
            <div
              role="group"
              aria-label={t('customizations.label')}
              className="grid gap-3 sm:grid-cols-2"
            >
              {customizations.map((c) => {
                const id = `${groupId}-${c.id}`;
                return (
                  <div key={c.id} className="flex items-start gap-2 rounded-md border p-3">
                    <Checkbox
                      id={id}
                      className="mt-0.5"
                      aria-describedby={`${id}-meta`}
                      checked={chosen.has(c.id)}
                      disabled={readOnly}
                      onCheckedChange={(next) => toggle(c.id, next === true)}
                    />
                    <div className="flex flex-col gap-0.5">
                      <Label htmlFor={id} className="font-normal">
                        {c.label}
                      </Label>
                      <span id={`${id}-meta`} className="text-muted-foreground text-xs">
                        <code>{c.key}</code> ·{' '}
                        {c.isRequired ? t('customizations.required') : t('customizations.optional')}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        }}
      />
      {form.formState.errors.defaultCustomizationIds?.message ? (
        <p className="text-destructive text-sm">
          {form.formState.errors.defaultCustomizationIds.message}
        </p>
      ) : null}
    </FormSection>
  );
}
